import { useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  Badge,
  List,
  ListItem,
  ListIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Flex,
  keyframes,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { motion, useInView } from 'framer-motion';
import {
  FiFeather,
  FiBookOpen,
  FiSend,
  FiType,
  FiTarget,
  FiSliders,
  FiPlay,
  FiArrowRight,
  FiLayers,
  FiZap,
  FiEdit3,
} from 'react-icons/fi';
import { useNavigate, useOutletContext } from 'react-router-dom';

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(224, 82, 82, 0.15); }
  50% { box-shadow: 0 0 40px rgba(224, 82, 82, 0.3); }
`;

const FadeInSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <MotionBox
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </MotionBox>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  useOutletContext();
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box bg="#F5F3F0" minH="100vh" overflow="hidden">
      {/* Hero Section */}
      <Box position="relative" overflow="hidden">
        {/* Subtle background texture */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity={0.4}
          bgImage="radial-gradient(circle at 20% 50%, rgba(224, 82, 82, 0.06) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(201, 69, 69, 0.04) 0%, transparent 40%), radial-gradient(circle at 60% 80%, rgba(224, 82, 82, 0.03) 0%, transparent 45%)"
          pointerEvents="none"
        />

        <Container maxW="1100px" px={8} pt={{ base: 16, md: 28 }} pb={{ base: 16, md: 24 }} position="relative">
          <VStack spacing={8} textAlign="center">
            <MotionBox
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <Badge
                bg="white"
                color="#E05252"
                borderRadius="full"
                px={5}
                py={2}
                fontSize="xs"
                fontWeight="600"
                textTransform="none"
                boxShadow="0 2px 12px rgba(0,0,0,0.06)"
                border="1px solid"
                borderColor="#F0E0E0"
                letterSpacing="0.02em"
                display="inline-flex"
                alignItems="center"
              >
                <HStack spacing={2} align="center">
                  <Icon as={FiFeather} boxSize={3.5} />
                  <Text lineHeight="1">AI-powered creative writing</Text>
                </HStack>
              </Badge>
            </MotionBox>

            <MotionHeading
              fontWeight="800"
              fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
              color="#1A1A1A"
              letterSpacing="-0.04em"
              lineHeight="1.05"
              maxW="800px"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
            >
              Write stories that
              <br />
              <Text as="span" color="#E05252">come alive</Text>
            </MotionHeading>

            <MotionText
              fontSize={{ base: "lg", md: "xl" }}
              color="#52525B"
              maxW="540px"
              lineHeight="1.8"
              fontWeight="400"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              An intuitive writing workspace with granular AI control.
              Organize chapters, generate scenes, fine-tune your voice — all in one place.
            </MotionText>

            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
                <Button
                  size="lg"
                  bg="#E05252"
                  color="white"
                  px={10}
                  py={7}
                  fontSize="md"
                  fontWeight="600"
                  borderRadius="xl"
                  boxShadow="0 4px 16px rgba(224, 82, 82, 0.25)"
                  _hover={{ bg: '#C94545', transform: 'translateY(-2px)', boxShadow: '0 8px 28px rgba(224, 82, 82, 0.35)' }}
                  transition="all 0.25s"
                  onClick={() => navigate('/register')}
                  rightIcon={<Icon as={FiArrowRight} />}
                >
                  Start Writing — Free
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  color="#52525B"
                  px={8}
                  py={7}
                  fontSize="md"
                  fontWeight="500"
                  borderRadius="xl"
                  _hover={{ bg: 'rgba(0,0,0,0.04)', color: '#1A1A1A' }}
                  transition="all 0.2s"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
              </HStack>
              <Button
                mt={3}
                variant="outline"
                size="lg"
                borderColor="#E4E4E7"
                color="#52525B"
                px={8}
                py={7}
                fontSize="md"
                fontWeight="500"
                borderRadius="xl"
                _hover={{ borderColor: '#E05252', color: '#E05252', bg: '#FEF2F2' }}
                transition="all 0.2s"
                onClick={() => navigate('/explore')}
                leftIcon={<Icon as={FiBookOpen} />}
              >
                Explore stories by other writers
              </Button>
            </MotionBox>
          </VStack>
        </Container>
      </Box>

      {/* About Section */}
      <Box bg="white" py={{ base: 12, md: 20 }} borderTop="1px solid" borderBottom="1px solid" borderColor="#E4E4E7" position="relative">
        <Container maxW="1100px" px={8}>
          <FadeInSection>
            <VStack spacing={4} textAlign="center" mb={10}>
              <Text fontSize="sm" fontWeight="600" color="#E05252" textTransform="uppercase" letterSpacing="0.1em">
                About
              </Text>
              <Heading fontSize={{ base: "2xl", md: "3xl" }} color="#1A1A1A" fontWeight="700" letterSpacing="-0.03em">
                About Writewizard
              </Heading>
            </VStack>
          </FadeInSection>

          <FadeInSection delay={0.1}>
            <VStack spacing={6} maxW="700px" mx="auto">
              <Text fontSize="md" color="#52525B" lineHeight="1.8" textAlign="center">
                Welcome to Writewizard, your AI-powered creative writing companion. This platform
                combines the power of artificial intelligence with human creativity to help you craft
                compelling stories, develop rich characters, and build immersive worlds.
              </Text>
              <Text fontSize="md" color="#52525B" lineHeight="1.8" textAlign="center">
                Whether you're a seasoned author or just starting your writing journey, Writewizard
                provides the tools and support you need to bring your stories to life. Our intelligent
                assistant helps you overcome writer's block, suggests plot developments, and ensures
                your narrative flows seamlessly.
              </Text>
              <Text fontSize="md" color="#52525B" lineHeight="1.8" textAlign="center">
                Join a growing community of writers and discover how AI can enhance your creative
                process. Writewizard is more than just a writing tool — it's your partner in
                storytelling.
              </Text>
            </VStack>
          </FadeInSection>
        </Container>
      </Box>

      {/* Product Preview / How It Works */}
      <Box py={{ base: 12, md: 20 }} position="relative">
        <Container maxW="1100px" px={8}>
          <FadeInSection>
            <VStack spacing={4} textAlign="center" mb={16}>
              <Text fontSize="sm" fontWeight="600" color="#E05252" textTransform="uppercase" letterSpacing="0.1em">
                How it works
              </Text>
              <Heading fontSize={{ base: "2xl", md: "3xl" }} color="#1A1A1A" fontWeight="700" letterSpacing="-0.03em">
                From blank page to finished story
              </Heading>
              <Text fontSize="md" color="#52525B" maxW="480px">
                Writewizard gives you a structured workspace with AI woven into every step.
              </Text>
            </VStack>
          </FadeInSection>

          <VStack spacing={0} position="relative">
            {/* Connecting line */}
            <Box
              display={{ base: "none", md: "block" }}
              position="absolute"
              left="50%"
              top="40px"
              bottom="40px"
              w="2px"
              bg="linear-gradient(to bottom, transparent, #E4E4E7 10%, #E4E4E7 90%, transparent)"
              transform="translateX(-50%)"
              zIndex={0}
            />

            <StepCard
              step="1"
              icon={FiBookOpen}
              title="Organize your story"
              description="Create books, add chapters, and break your writing into manageable snippets. Everything stays structured and easy to navigate."
              align="right"
            />
            <StepCard
              step="2"
              icon={FiZap}
              title="Generate with AI"
              description="Describe what you want — a tense dialogue, a vivid landscape, a plot twist. The AI generates it in your style, and you stay in control."
              align="left"
            />
            <StepCard
              step="3"
              icon={FiEdit3}
              title="Highlight & rewrite"
              description="Select any passage and ask AI to rewrite it. Make it more dramatic, more concise, change the tone — or give custom instructions."
              align="right"
            />
            <StepCard
              step="4"
              icon={FiSend}
              title="Publish & share"
              description="When you're ready, share your story with the Writewizard community. Discover new reads from fellow authors."
              align="left"
            />
          </VStack>
        </Container>
      </Box>

      {/* Features Grid */}
      <Box bg="white" py={{ base: 16, md: 24 }} borderTop="1px solid" borderBottom="1px solid" borderColor="#E4E4E7">
        <Container maxW="1100px" px={8}>
          <FadeInSection>
            <VStack spacing={4} textAlign="center" mb={14}>
              <Text fontSize="sm" fontWeight="600" color="#E05252" textTransform="uppercase" letterSpacing="0.1em">
                Features
              </Text>
              <Heading fontSize={{ base: "2xl", md: "3xl" }} color="#1A1A1A" fontWeight="700" letterSpacing="-0.03em">
                Everything you need to write your story
              </Heading>
            </VStack>
          </FadeInSection>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} w="100%">
            <FadeInSection delay={0}>
              <FeatureCard
                icon={FiFeather}
                title="AI-Assisted Writing"
                description="Generate text, rewrite passages, and get suggestions from AI that understands your story's context and tone."
              />
            </FadeInSection>
            <FadeInSection delay={0.08}>
              <FeatureCard
                icon={FiLayers}
                title="Organize Your Work"
                description="Structure your stories with books, chapters, and snippets. Keep everything in one place, always easy to find."
              />
            </FadeInSection>
            <FadeInSection delay={0.16}>
              <FeatureCard
                icon={FiSend}
                title="Publish & Share"
                description="Share your stories with a community of fellow writers. Discover new reads from other authors."
              />
            </FadeInSection>
            <FadeInSection delay={0.08}>
              <FeatureCard
                icon={FiType}
                title="Highlight & Rewrite"
                description="Select any passage and rewrite it with AI. Make it more dramatic, concise, or give custom instructions."
              />
            </FadeInSection>
            <FadeInSection delay={0.16}>
              <FeatureCard
                icon={FiTarget}
                title="Writing Practice"
                description="Sharpen your craft with AI-generated writing exercises and get personalized feedback on your work."
              />
            </FadeInSection>
            <FadeInSection delay={0.24}>
              <FeatureCard
                icon={FiSliders}
                title="Style Control"
                description="Fine-tune tone, point of view, writing style, creativity level, and language for every generation."
              />
            </FadeInSection>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Tutorial Section */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="900px" px={8}>
          <FadeInSection>
            <VStack spacing={8} textAlign="center">
              <VStack spacing={4}>
                <Text fontSize="sm" fontWeight="600" color="#E05252" textTransform="uppercase" letterSpacing="0.1em">
                  See it in action
                </Text>
                <Heading fontSize={{ base: "2xl", md: "3xl" }} color="#1A1A1A" fontWeight="700" letterSpacing="-0.03em">
                  Watch how it works
                </Heading>
                <Text fontSize="md" color="#52525B" maxW="460px">
                  A quick walkthrough showing how Writewizard takes you from idea to finished story.
                </Text>
              </VStack>

              <Box
                as="button"
                onClick={onOpen}
                w="100%"
                maxW="700px"
                bg="#1A1A1A"
                borderRadius="2xl"
                overflow="hidden"
                position="relative"
                paddingBottom="56.25%"
                cursor="pointer"
                _hover={{ '& > .play-btn': { transform: 'translate(-50%, -50%) scale(1.1)', boxShadow: '0 8px 30px rgba(224, 82, 82, 0.5)' } }}
                transition="all 0.3s"
                boxShadow="0 20px 60px rgba(0, 0, 0, 0.15)"
              >
                {/* Decorative grid pattern on video placeholder */}
                <Box
                  position="absolute"
                  inset="0"
                  opacity={0.06}
                  bgImage="linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)"
                  bgSize="40px 40px"
                />
                <Box
                  className="play-btn"
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  bg="#E05252"
                  borderRadius="full"
                  w="80px"
                  h="80px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 4px 24px rgba(224, 82, 82, 0.4)"
                  transition="all 0.3s"
                  animation={`${pulseGlow} 3s ease-in-out infinite`}
                >
                  <Icon as={FiPlay} boxSize={7} color="white" ml="3px" />
                </Box>
                <Text
                  position="absolute"
                  bottom="24px"
                  left="50%"
                  transform="translateX(-50%)"
                  color="whiteAlpha.700"
                  fontSize="sm"
                  fontWeight="500"
                  letterSpacing="0.03em"
                >
                  Tutorial
                </Text>
              </Box>

              <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
                <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
                <ModalContent bg="#1A1A1A" borderRadius="xl">
                  <ModalHeader color="white" fontSize="md" fontWeight="600">Tutorial</ModalHeader>
                  <ModalCloseButton color="white" />
                  <ModalBody pb={6}>
                    <Box position="relative" paddingBottom="56.25%" h={0}>
                      <iframe
                        src="https://www.youtube.com/embed/GgJ8jqVHeiY"
                        title="How to use Writewizard"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          borderRadius: "8px"
                        }}
                      />
                    </Box>
                  </ModalBody>
                </ModalContent>
              </Modal>
            </VStack>
          </FadeInSection>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box bg="white" py={{ base: 16, md: 24 }} borderTop="1px solid" borderBottom="1px solid" borderColor="#E4E4E7">
        <Container maxW="900px" px={8}>
          <VStack spacing={12}>
            <FadeInSection>
              <VStack spacing={4} textAlign="center">
                <Text fontSize="sm" fontWeight="600" color="#E05252" textTransform="uppercase" letterSpacing="0.1em">
                  Pricing
                </Text>
                <Heading fontSize={{ base: "2xl", md: "3xl" }} color="#1A1A1A" fontWeight="700" letterSpacing="-0.03em">
                  Simple, transparent pricing
                </Heading>
                <Text fontSize="md" color="#52525B">
                  Start for free, upgrade when you're ready.
                </Text>
              </VStack>
            </FadeInSection>

            <FadeInSection>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="100%">
                {/* Free Plan */}
                <Box
                  border="1px solid"
                  borderColor="#E4E4E7"
                  borderRadius="2xl"
                  p={8}
                  bg="white"
                  transition="all 0.25s"
                  _hover={{ boxShadow: '0 8px 30px rgba(0,0,0,0.06)', borderColor: '#D4D4D8' }}
                >
                  <VStack align="stretch" spacing={6}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="700" color="#A1A1AA" textTransform="uppercase" letterSpacing="wide">Free</Text>
                      <HStack align="baseline" spacing={1}>
                        <Heading fontSize="4xl" color="#1A1A1A" fontWeight="700">$0</Heading>
                        <Text color="#A1A1AA" fontSize="sm">/month</Text>
                      </HStack>
                      <Text fontSize="sm" color="#52525B">Get started with the basics</Text>
                    </VStack>
                    <Box h="1px" bg="#F4F4F5" />
                    <List spacing={3}>
                      <PricingFeature>Create and organize stories</PricingFeature>
                      <PricingFeature>Chapter & snippet management</PricingFeature>
                      <PricingFeature>Publish to the community</PricingFeature>
                      <PricingFeature>Limited AI generations</PricingFeature>
                    </List>
                    <Button
                      variant="outline"
                      borderColor="#E4E4E7"
                      color="#52525B"
                      size="lg"
                      borderRadius="xl"
                      fontWeight="500"
                      _hover={{ bg: '#FAFAF9', borderColor: '#A1A1AA' }}
                      transition="all 0.2s"
                      onClick={() => navigate('/register')}
                    >
                      Get Started
                    </Button>
                  </VStack>
                </Box>

                {/* Pro Plan */}
                <Box
                  border="2px solid"
                  borderColor="#E05252"
                  borderRadius="2xl"
                  p={8}
                  position="relative"
                  bg="white"
                  boxShadow="0 8px 30px rgba(224, 82, 82, 0.1)"
                  transition="all 0.25s"
                  _hover={{ boxShadow: '0 12px 40px rgba(224, 82, 82, 0.18)' }}
                >
                  <Badge
                    position="absolute"
                    top="-12px"
                    left="50%"
                    transform="translateX(-50%)"
                    bg="#E05252"
                    color="white"
                    borderRadius="full"
                    px={4}
                    py={1}
                    fontSize="xs"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                  >
                    Popular
                  </Badge>
                  <VStack align="stretch" spacing={6}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="700" color="#E05252" textTransform="uppercase" letterSpacing="wide">Pro</Text>
                      <HStack align="baseline" spacing={1}>
                        <Heading fontSize="4xl" color="#1A1A1A" fontWeight="700">€10</Heading>
                        <Text color="#A1A1AA" fontSize="sm">/month</Text>
                      </HStack>
                      <Text fontSize="sm" color="#52525B">For serious writers</Text>
                    </VStack>
                    <Box h="1px" bg="#FEF2F2" />
                    <List spacing={3}>
                      <PricingFeature>Everything in Free</PricingFeature>
                      <PricingFeature>Unlimited AI generations</PricingFeature>
                      <PricingFeature>Highlight & rewrite with AI</PricingFeature>
                      <PricingFeature>Advanced style controls</PricingFeature>
                      <PricingFeature>Writing practice & feedback</PricingFeature>
                      <PricingFeature>Choice of AI model</PricingFeature>
                    </List>
                    <Button
                      bg="#E05252"
                      color="white"
                      size="lg"
                      borderRadius="xl"
                      fontWeight="600"
                      boxShadow="0 4px 16px rgba(224, 82, 82, 0.25)"
                      _hover={{ bg: '#C94545', transform: 'translateY(-1px)', boxShadow: '0 8px 24px rgba(224, 82, 82, 0.35)' }}
                      transition="all 0.25s"
                      onClick={() => navigate('/register')}
                    >
                      Start Free Trial
                    </Button>
                  </VStack>
                </Box>
              </SimpleGrid>
            </FadeInSection>
          </VStack>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box py={{ base: 16, md: 24 }} position="relative" overflow="hidden">
        {/* Background decoration */}
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          w="600px"
          h="600px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(224, 82, 82, 0.06) 0%, transparent 70%)"
          pointerEvents="none"
        />

        <Container maxW="700px" px={8} position="relative">
          <FadeInSection>
            <VStack spacing={8} textAlign="center">
              <VStack spacing={4}>
                <Heading fontSize={{ base: "2xl", md: "3xl" }} color="#1A1A1A" fontWeight="700" letterSpacing="-0.03em">
                  Ready to start writing?
                </Heading>
                <Text fontSize="lg" color="#52525B" maxW="440px">
                  Join writers who use Writewizard to bring their stories to life. No credit card required.
                </Text>
              </VStack>
              <HStack spacing={4} flexWrap="wrap" justify="center">
                <Button
                  size="lg"
                  bg="#E05252"
                  color="white"
                  px={10}
                  py={7}
                  fontSize="md"
                  fontWeight="600"
                  borderRadius="xl"
                  boxShadow="0 4px 16px rgba(224, 82, 82, 0.25)"
                  _hover={{ bg: '#C94545', transform: 'translateY(-2px)', boxShadow: '0 8px 28px rgba(224, 82, 82, 0.35)' }}
                  transition="all 0.25s"
                  onClick={() => navigate('/register')}
                  rightIcon={<Icon as={FiArrowRight} />}
                >
                  Create Free Account
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  color="#52525B"
                  px={8}
                  py={7}
                  fontSize="md"
                  fontWeight="500"
                  borderRadius="xl"
                  _hover={{ bg: 'rgba(0,0,0,0.04)', color: '#1A1A1A' }}
                  transition="all 0.2s"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
              </HStack>
            </VStack>
          </FadeInSection>
        </Container>
      </Box>

      {/* Footer */}
      <Box bg="white" py={10} borderTop="1px solid" borderColor="#E4E4E7">
        <Container maxW="1100px" px={8}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "center", md: "center" }}
            gap={6}
          >
            <VStack align={{ base: "center", md: "start" }} spacing={1}>
              <HStack spacing={0}>
                <Text fontWeight="700" fontSize="md" color="#C94545">Write</Text>
                <Text fontWeight="700" fontSize="md" color="#E05252">wizard</Text>
              </HStack>
              <Text fontSize="xs" color="#A1A1AA">Your AI-powered writing companion</Text>
            </VStack>
            <HStack spacing={8}>
              <FooterLink onClick={() => navigate('/explore')}>Explore</FooterLink>
              <FooterLink onClick={() => window.location.href = 'mailto:support@writewizard.org'}>Contact</FooterLink>
            </HStack>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
};

/* ─── Step Card (How It Works) ─── */
const StepCard = ({ step, icon, title, description, align }) => {
  const isRight = align === 'right';
  return (
    <FadeInSection delay={Number(step) * 0.1}>
      <Flex
        direction={{ base: "column", md: isRight ? "row" : "row-reverse" }}
        align="center"
        gap={{ base: 6, md: 12 }}
        py={8}
        position="relative"
        zIndex={1}
      >
        {/* Content side */}
        <Box flex="1" textAlign={{ base: "center", md: isRight ? "right" : "left" }}>
          <Text fontSize="xs" fontWeight="700" color="#E05252" textTransform="uppercase" letterSpacing="0.12em" mb={2}>
            Step {step}
          </Text>
          <Heading fontSize={{ base: "xl", md: "2xl" }} color="#1A1A1A" fontWeight="700" letterSpacing="-0.02em" mb={3}>
            {title}
          </Heading>
          <Text fontSize="md" color="#52525B" lineHeight="1.7" maxW="380px" mx={{ base: "auto", md: isRight ? "0" : "0" }} ml={{ md: isRight ? "auto" : "0" }}>
            {description}
          </Text>
        </Box>

        {/* Icon center */}
        <Flex
          w="64px"
          h="64px"
          borderRadius="2xl"
          bg="white"
          border="2px solid"
          borderColor="#F0E0E0"
          align="center"
          justify="center"
          flexShrink={0}
          boxShadow="0 4px 16px rgba(224, 82, 82, 0.08)"
          animation={`${floatAnimation} 4s ease-in-out infinite`}
          sx={{ animationDelay: `${Number(step) * 0.5}s` }}
        >
          <Icon as={icon} boxSize={6} color="#E05252" />
        </Flex>

        {/* Spacer for the other side */}
        <Box flex="1" display={{ base: "none", md: "block" }} />
      </Flex>
    </FadeInSection>
  );
};

/* ─── Feature Card ─── */
const FeatureCard = ({ icon, title, description }) => (
  <VStack
    bg="#FAFAF9"
    p={8}
    borderRadius="2xl"
    spacing={4}
    align="start"
    textAlign="left"
    border="1px solid"
    borderColor="#F4F4F5"
    transition="all 0.25s"
    _hover={{
      borderColor: '#E4E4E7',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)',
      transform: 'translateY(-2px)',
    }}
    h="100%"
  >
    <Flex
      w="44px"
      h="44px"
      borderRadius="xl"
      bg="#FEF2F2"
      align="center"
      justify="center"
    >
      <Icon as={icon} boxSize={5} color="#E05252" strokeWidth={2.5} />
    </Flex>
    <Heading fontSize="md" color="#1A1A1A" fontWeight="600">
      {title}
    </Heading>
    <Text fontSize="sm" color="#52525B" lineHeight="1.7">
      {description}
    </Text>
  </VStack>
);

/* ─── Pricing Feature ─── */
const PricingFeature = ({ children }) => (
  <ListItem fontSize="sm" color="#52525B">
    <ListIcon as={CheckCircleIcon} color="#E05252" />
    {children}
  </ListItem>
);

/* ─── Footer Link ─── */
const FooterLink = ({ children, onClick }) => (
  <Text
    as="button"
    fontSize="sm"
    color="#A1A1AA"
    fontWeight="500"
    _hover={{ color: '#52525B' }}
    transition="color 0.2s"
    onClick={onClick}
  >
    {children}
  </Text>
);

export default Landing;
